import { useState, useEffect, useRef } from 'react'

import { decodeSvgBuffer } from '../../../shared/utils/svgUtils.js'
import { Logger }          from '../../../shared/utils/logger.js'
import { FETCH_STATUS }    from '../../../shared/constants/fetchStatus.js'

// ── URL builder ────────────────────────────────────────────────────────────────

/**
 * Builds the absolute URL for the static visualization SVG file.
 *
 * Convention (mirrors generator output structure):
 * - Desktop → `{dataUrl}site/vis/main.svg`
 * - Mobile  → `{dataUrl}site/vis/main_mobile.svg`
 *
 * @param {string}  dataUrl
 * @param {boolean} isMobile
 * @returns {string|null}  null when dataUrl is missing
 */
export function buildVisualizationSvgUrl(dataUrl, isMobile) {
  if (!dataUrl) return null
  const base     = dataUrl.endsWith('/') ? dataUrl : dataUrl + '/'
  const fileName = isMobile ? 'main_mobile' : 'main'
  return `${base}website/vis/${fileName}.svg`
  // return `${base}site/vis/${fileName}.svg`  // OLD VERSION WITH vis/ in site/ directory
}

// ── Href fixer ────────────────────────────────────────────────────────────────

/**
 * Rewrites relative `href` and `xlink:href` values in SVG markup to absolute
 * URLs, setting both attributes for SVG 1.1 / 2.0 compatibility.
 *
 * Also normalizes `preserveAspectRatio` to `"xMidYMid slice"` so the SVG
 * behaves like CSS `background-size: cover` (fills canvas and crops overflow).
 *
 * @param {string} svgString  — raw SVG markup
 * @param {string} dataUrl    — project base data URL
 * @returns {string}
 */
export function fixVisualizationSvgHrefs(svgString, dataUrl) {
  const base    = dataUrl.endsWith('/') ? dataUrl : dataUrl + '/'
  const visBase = `${base}website/vis/`
  // const visBase = `${base}site/vis/`  // OLD VERSION WITH vis/ in site/ directory

  const parser = new DOMParser()
  const doc    = parser.parseFromString(svgString, 'image/svg+xml')
  const svgEl  = doc.querySelector('svg')

  if (!svgEl) return svgString

  const absHref = (value) => {
    if (!value) return value
    if (/^(https?:|\/\/|data:|#)/i.test(value)) return value
    return `${visBase}${value}`
  }

  const linked = doc.querySelectorAll('[href], [xlink\\:href]')
  linked.forEach((el) => {
    const href = el.getAttribute('href')
    const xhref = el.getAttribute('xlink:href')
    const src = href || xhref
    if (!src) return
    const fixed = absHref(src)
    el.setAttribute('href', fixed)
    el.setAttribute('xlink:href', fixed)
  })

  // Inline SVG should fill parent box; crop overflow like CSS cover.
  svgEl.setAttribute('preserveAspectRatio', 'xMidYMid slice')
  svgEl.setAttribute('width', '100%')
  svgEl.setAttribute('height', '100%')

  return new XMLSerializer().serializeToString(doc)
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * useVisualizationSvg
 *
 * Fetches the static visualization SVG (desktop: `main.svg`, mobile:
 * `main_mobile.svg`). Applies href fix and preserveAspectRatio normalization
 * before returning the markup.
 *
 * Results are cached in a `useRef` map keyed by URL — switching breakpoints
 * and back will not re-fetch.
 *
 * @param {object}  params
 * @param {string}  params.dataUrl   — project data base URL
 * @param {boolean} params.isMobile  — selects main_mobile.svg vs main.svg
 *
 * @returns {{ svgString: string|null, svgStatus: string }}
 *   `svgString` — processed SVG markup ready for `dangerouslySetInnerHTML`
 *   `svgStatus` — one of FETCH_STATUS.LOADING | READY | ERROR
 */
export function useVisualizationSvg({ dataUrl, isMobile }) {
  const cache = useRef({})

  const [svgString, setSvgString] = useState(null)
  const [svgStatus, setSvgStatus] = useState(FETCH_STATUS.LOADING)

  useEffect(() => {
    const url = buildVisualizationSvgUrl(dataUrl, isMobile)

    if (!url) {
      setSvgString(null)
      setSvgStatus(FETCH_STATUS.LOADING)
      return
    }

    if (url in cache.current) {
      setSvgString(cache.current[url])
      setSvgStatus(FETCH_STATUS.READY)
      return
    }

    let cancelled = false
    setSvgStatus(FETCH_STATUS.LOADING)

    async function fetchSvg() {
      try {
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${url}`)
        }

        const contentType = response.headers.get('content-type') ?? ''
        if (contentType.includes('text/html')) {
          throw new Error(`Expected SVG but got HTML — file may not exist: ${url}`)
        }

        const buffer  = await response.arrayBuffer()
        const raw     = decodeSvgBuffer(buffer)
        const fixed   = fixVisualizationSvgHrefs(raw, dataUrl)

        cache.current[url] = fixed

        if (!cancelled) {
          setSvgString(fixed)
          setSvgStatus(FETCH_STATUS.READY)
        }
      } catch (err) {
        if (!cancelled) {
          Logger.error('[useVisualizationSvg] Failed to fetch SVG:', err)
          setSvgString(null)
          setSvgStatus(FETCH_STATUS.ERROR)
        }
      }
    }

    fetchSvg()
    return () => { cancelled = true }
  }, [dataUrl, isMobile])

  return { svgString, svgStatus }
}
